import { createFileRoute } from "@tanstack/react-router";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { useAppSelector } from "../../../../redux/hooks";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "../../../../utils/dashboardLinks";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import { type TargetQueryV4Row } from "../../../../modules/perf_reports/api/target/v4TargetReportByTarget";
import { useBaseTargetColumns } from "../../../../modules/perf_reports/target_report/useBaseTargetColumns";
import { createColumnBuilder } from "../../../../components/table/columnBuilder";
import { css } from "../../../../../styled-system/css";
import { idsToVisibilityState, selectionStateToSelectedIds } from "../../../../components/table/utils";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { useGetV4TargetReportByDate } from "../../../../modules/perf_reports/api/target/v4TargetReportByDate";
import { useQuery } from "@tanstack/react-query";
import { useGetV4TargetReportTotals } from "../../../../modules/perf_reports/api/target/v4TargetReportTotals";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { Flex } from "../../../../../styled-system/jsx";
import { toTitleCase } from "../../../../utils/formatText";
import { TargetBadges } from "../../../../components/custom/category/advertisements/TargetBadges";
import { Field } from "@formily/react";
import { formatCurrency } from "../../../../utils/formatNumber";
import { useInitialValueSync } from "../../../../utils/formily";
import { CurrencyInput } from "../../../../components/fields/formily/NumberInput";
import { AD_STATUSES, getMinBid } from "../../../../components/core/blocks/AdPlan/constants";
import { AD_STATE_OPTIONS } from "../../../../modules/advertising/filters/targetLifecycleFilters";
import type { TSelector } from "../../../../i18n/types";
import KeywordCell from "../../../../components/custom/category/advertisements/KeywordCell";
import TrellisLink from "../../../../components/core/basic/TrellisLink";
import { getDashboardTo } from "../../../../views/AdvertisingOverview";
import { useCategoriesQuery } from "../../../../hooks/useCategories";
import useQueryParams from "../../../../hooks/useQueryParams";
import { useFormatTargetText } from "../../../../utils/useFormatTargetText";
import { Tabs, TabList, Tab } from "../../../../components/core/basic/Tabs";
import { Select } from "../../../../components/fields/base/Select";
import { FaArrowUp, FaArrowDown, FaEquals } from "react-icons/fa6";
import { HoverInfo } from "../../../../components/core/basic/Tooltip";
import {
  DEFAULT_TARGET_REPORT_SELECTED_COLUMNS,
  DIMENSION_TYPES,
} from "../../../../modules/str/utils/reportingConstants";
import { KeywordTargetReportTable } from "../../../../modules/perf_reports/target_report/KeywordTargetReportTable";
import { ProductTargetReportTable } from "../../../../modules/perf_reports/target_report/ProductTargetReportTable";

const columnHelper = createColumnBuilder<TargetQueryV4Row>();

const AD_STATUS_LABEL_BY_VALUE = Object.fromEntries(
  Object.values(AD_STATUSES).map((status) => [status.value, status.label]),
) as Record<number, TSelector>;

const AD_STATE_LABEL_BY_VALUE = Object.fromEntries(
  AD_STATE_OPTIONS.filter((option) => typeof option.value === "number").map((option) => [option.value, option.label]),
) as Record<number, TSelector>;

type BidTrend = "Increasing" | "Decreasing" | "Stable";

const BID_TREND_ICON: Record<BidTrend, React.JSX.Element> = {
  Increasing: <FaArrowUp />,
  Decreasing: <FaArrowDown />,
  Stable: <FaEquals />,
};

const TargetReportBody = () => {
  const { t } = useTranslation(["perf_reports", "advertising"]);
  const [targetType, setTargetType] = useQueryParams("tab", "keyword");
  const [dimensions, setDimensions] = useQueryParams("dims", DIMENSION_TYPES.KEYWORD_ID);

  const safeDimensions = dimensions || DIMENSION_TYPES.KEYWORD_ID;

  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");

  const standardMetricIds = useStandardEntityMetrics("target");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const baseColumns = useBaseTargetColumns(targetType ?? "keyword");
  const formatTargetText = useFormatTargetText();

  const categoriesQuery = useCategoriesQuery();

  const categoryFilterOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.perf_reports["All Categories"]) },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [categoriesQuery.data, t],
  );

  const columns = useMemo(() => {
    const allColumns = [
      columnHelper.accessor("text", {
        id: "text",
        header: targetType === "keyword" ? t(($) => $.perf_reports["Keyword"]) : t(($) => $.perf_reports["Target"]),
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: (props) => {
          const { text, ad_id, refinements } = props.row.original;
          const adsMapping =
            (
              props.table.options.meta as {
                adsMapping?: Record<string, { ad_id: number; ad_name: string; managed: string; ad_type?: number }>;
              }
            ).adsMapping ?? {};
          const adName = ad_id ? (adsMapping[ad_id]?.ad_name ?? "") : "";

          return (
            <div className={css({ paddingLeft: "small" })}>
              <KeywordCell
                value={formatTargetText(text, { ...refinements, marketPlace: marketPlace })}
                columnWidth={300}
              />
              {(
                [DIMENSION_TYPES.KEYWORD_ID, DIMENSION_TYPES.TARGET_ID].includes(safeDimensions) ||
                targetType === "target"
              ) ?
                <>
                  <br />
                  <TrellisLink
                    to={getDashboardTo({
                      type: "advertisement",
                      id: ad_id,
                    })}
                    target={"_blank"}
                    className={css({
                      paddingRight: "medium",
                      textDecoration: "none",
                      fontSize: "medium",
                      fontWeight: 500,
                    })}
                  >
                    <small>{adName}</small>
                  </TrellisLink>
                  <br />
                  <TargetBadges target={props.row.original} />
                </>
              : null}
            </div>
          );
        },
      }),
      // Invisible columns to support filtering
      columnHelper.accessor((row) => row.ad_id, {
        id: "ad_id",
        header: t(($) => $.perf_reports["Media Plan"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "media-plan",
          },
        },
      }),
      columnHelper.accessor((row) => row.category_id, {
        id: "category_id",
        header: t(($) => $.perf_reports["Category"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row.has_manual_bid, {
        id: "has_manual_bid",
        header: t(($) => $.perf_reports["Fixed Bid"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "single-select",
          },
        },
      }),
      columnHelper.accessor((row) => row.tags, {
        id: tagsEnhancedEnabled ? "tags_enhanced.id" : "tags",
        header: t(($) => $.perf_reports["Tags"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select" as const,
            label: t(($) => $.perf_reports["Tags"]),
          },
        },
      }),
      // Invisible columns end
      columnHelper.accessor("bid_resolved", {
        id: "bid_resolved",
        header: t(($) => $.perf_reports["Bid"]),
        size: 90,
        cell: function BidCell({ getValue, column, row, table }) {
          const mp = useAppSelector((state) => state.marketPlace.marketPlace);
          useInitialValueSync(column.id, getValue());
          const meta = table.options.meta as {
            adsMapping?: Record<string, { ad_id: number; ad_name: string; managed: string; ad_type?: number }>;
          };
          const adType = meta.adsMapping?.[row.original.ad_id]?.ad_type;
          const minBid = getMinBid(adType);
          return (
            <Field
              name={`${column.id}`}
              initialValue={getValue()}
              validator={(value: number) => {
                if (Number.isNaN(value)) {
                  return t(($) => $.advertising["Enter a bid value"]);
                }
                if (value < minBid) {
                  return t(($) => $.perf_reports["Bid must be at least {amount}"], {
                    amount: formatCurrency(minBid, {
                      marketPlace: mp,
                      decimalPlaces: 2,
                    }),
                  });
                }
              }}
              component={[
                CurrencyInput,
                {
                  variant: "inline",
                  css: css.raw({ marginX: "small" }),
                },
              ]}
            />
          );
        },
        meta: {
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["The current bid for the advertised {targetType}"], { targetType }),
          },
          columnFilterOptions: {
            format: "currency",
          },
          justify: "center",
        },
      }),

      columnHelper.accessor("bid_trend_latest", {
        id: "bid_trend_latest",
        header: t(($) => $.perf_reports["Recent Bid Trend"]),
        minSize: 90,
        maxSize: 90,
        cell: ({ getValue }) => {
          const trend = getValue();

          return (
            <HoverInfo
              iconCss={{
                borderRadius: "pill",
                padding: "small",
                color: "trellisPalette.16",
                fontSize: "small",
                fontWeight: "bold",
                textTransform: "none",
                backgroundColor: "trellisPalette.16/12",
                _hover: { backgroundColor: "trellisPalette.16/24" },
              }}
              icon={BID_TREND_ICON[trend]}
            >
              {t(($) => $.perf_reports.bid_trend[trend])}
            </HoverInfo>
          );
        },
        meta: {
          justify: "center",
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(
              ($) =>
                $.perf_reports["Bid trend direction considering the last two weeks of data in the selected period"],
            ),
          },
        },
      }),
      columnHelper.accessor("bid_trend_slope", {
        id: "bid_trend_slope",
        header: t(($) => $.perf_reports["Bid Trend"]),
        minSize: 90,
        maxSize: 90,
        cell: ({ getValue }) => {
          const trend = getValue();
          return (
            <HoverInfo
              iconCss={{
                borderRadius: "pill",
                padding: "small",
                color: "trellisPalette.16",
                fontSize: "small",
                fontWeight: "bold",
                textTransform: "none",
                backgroundColor: "trellisPalette.16/12",
                _hover: { backgroundColor: "trellisPalette.16/24" },
              }}
              icon={BID_TREND_ICON[trend]}
            >
              {t(($) => $.perf_reports.bid_trend[trend])}
            </HoverInfo>
          );
        },
        meta: {
          justify: "center",
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["Bid trend over the entire selected period"]),
          },
        },
      }),
      columnHelper.accessor((row) => row.ad_status, {
        id: "ad_status",
        header: t(($) => $.perf_reports["Ad Status"]),
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue();
          if (value == null) {
            return null;
          }
          const label = AD_STATUS_LABEL_BY_VALUE[value];
          return label ? t(label) : null;
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(($) => $.perf_reports["The status of the {targetType}'s advertisement"], { targetType }),
          },
        },
      }),
      columnHelper.accessor((row) => row.ad_state, {
        id: "ad_state",
        header: t(($) => $.perf_reports["Ad State"]),
        size: 120,
        cell: ({ getValue }) => {
          const value = getValue();
          if (value == null) {
            return null;
          }
          const label = AD_STATE_LABEL_BY_VALUE[value];
          return label ? t(label) : null;
        },
        meta: {
          columnFilterOptions: {
            format: "select",
            label: t(($) => $.perf_reports["Ad State"]),
          },
          columnSelectorOptions: {
            grouping: "configuration",
            description: t(
              ($) => $.perf_reports["Whether the {targetType}'s advertisement is active, paused, or ended"],
              { targetType },
            ),
          },
        },
      }),
      ...baseColumns,
    ];
    const excludedByDimension: Partial<Record<string, readonly string[]>> = {
      [DIMENSION_TYPES.TEXT]: [
        "state",
        "target_bid_state",
        "match_type",
        "ad_group_name",
        "ad_status",
        "ad_state",
        "creation_date",
        "source",
        "bid_resolved",
        "bid_trend_latest",
        "bid_trend_slope",
      ],
      [DIMENSION_TYPES.TEXT_WITH_MATCH]: [
        "state",
        "target_bid_state",
        "ad_group_name",
        "ad_status",
        "ad_state",
        "creation_date",
        "source",
        "bid_resolved",
        "bid_trend_latest",
        "bid_trend_slope",
      ],
      [DIMENSION_TYPES.KEYWORD_ID]: [],
    };

    const excluded = targetType === "target" ? [] : (excludedByDimension[safeDimensions] ?? []);

    const excludedSet = new Set(excluded);
    return allColumns.filter((col) => !(typeof col.id === "string" && excludedSet.has(col.id)));
  }, [marketPlace, targetType, safeDimensions, baseColumns, t, formatTargetText, tagsEnhancedEnabled]);

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = DEFAULT_TARGET_REPORT_SELECTED_COLUMNS;

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const targetReportInitialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["text"],
      end: [],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const {
    setters: targetReportSetters,
    state: targetReportState,
    initialState: targetReportInitialState,
  } = usePersistedTableState({
    persistKey: `advertised${toTitleCase(targetType)}Report-${targetType}`,
    initialState: targetReportInitialStateConfig,
  });

  const selectedIds = selectionStateToSelectedIds(targetReportState.rowSelection);
  const debounceSearch = useDebounce(targetReportState.globalFilter, 500);
  const selectedIdsFilter =
    selectedIds.length ?
      [
        {
          id: targetType === "target" ? DIMENSION_TYPES.TARGET_ID : DIMENSION_TYPES.KEYWORD_ID,
          value: { op: "anyof", value: selectedIds },
        },
      ]
    : [];

  const byDateOptionsGetter = useGetV4TargetReportByDate(targetType ?? "keyword");
  const targetsByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: [...targetReportState.columnFilters, ...selectedIdsFilter],
      search: debounceSearch,
    }),
  );
  const totalOptionsGetter = useGetV4TargetReportTotals(targetType ?? "keyword");
  const targetTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters: [...targetReportState.columnFilters, ...selectedIdsFilter],
      search: debounceSearch,
    }),
  );

  const metrics = useMemo(() => {
    return standardMetricIds.map((metricId) => getMetricSpec(metricId));
  }, [standardMetricIds]);

  return (
    <PageContainer withBreadcrumb={true}>
      <MetricsCard
        id={"targetReport"}
        title={t(($) => $.perf_reports["Performance Overview"])}
        totals={{
          data: targetTotalsReportQuery.data?.data,
          loading:
            targetTotalsReportQuery.isFetching &&
            (!targetTotalsReportQuery.data || targetTotalsReportQuery.isPlaceholderData),
        }}
        timeline={{
          data: targetsByDateReportQuery.data?.data ?? [],
          loading:
            targetsByDateReportQuery.isFetching &&
            (!targetsByDateReportQuery.data || targetsByDateReportQuery.isPlaceholderData),
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend"]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {longDate.format(start.toDate())}
            {" - "}
            {longDate.format(end.toDate())}
          </>
        }
        context={"target"}
      >
        <Flex>
          <Tabs
            selectedKey={targetType ?? "keyword"}
            onSelectionChange={(key) => {
              setTargetType(key as string);
            }}
          >
            <TabList>
              <Tab id="keyword">{t(($) => $.perf_reports["Keyword Targets"])}</Tab>
              <Tab id="target">{t(($) => $.perf_reports["Product Targets"])}</Tab>
            </TabList>
          </Tabs>
        </Flex>

        {targetType === "target" ?
          <ProductTargetReportTable
            state={targetReportState}
            initialState={targetReportInitialState}
            setters={targetReportSetters}
            key={`${targetType}-${DIMENSION_TYPES.TARGET_ID}`}
            categoryFilterOptions={categoryFilterOptions}
            columns={columns}
          />
        : <KeywordTargetReportTable
            dimensions={safeDimensions}
            state={targetReportState}
            initialState={targetReportInitialState}
            setters={targetReportSetters}
            key={`${targetType}-${safeDimensions}`}
            categoryFilterOptions={categoryFilterOptions}
            parentFilterControls={
              targetType === "keyword" ?
                <Flex
                  className={css({
                    alignSelf: "end",
                    width: "400px",
                    marginBottom: "large",
                    paddingX: "medium",
                  })}
                >
                  <Select
                    label={t(($) => $.perf_reports["Group By"])}
                    selectedKey={safeDimensions}
                    onSelectionChange={(key) => {
                      if (key !== null) {
                        setDimensions(key.toString());
                      }
                    }}
                    items={[
                      {
                        label: t(($) => $.perf_reports["Keyword Text, Match Type and Advertisement"]),
                        value: DIMENSION_TYPES.KEYWORD_ID,
                      },
                      {
                        label: t(($) => $.perf_reports["Keyword Text and Match Type"]),
                        value: DIMENSION_TYPES.TEXT_WITH_MATCH,
                      },
                      { label: t(($) => $.perf_reports["Keyword Text"]), value: DIMENSION_TYPES.TEXT },
                    ]}
                  />
                </Flex>
              : null
            }
            columns={columns}
          />
        }
      </MetricsCard>
    </PageContainer>
  );
};

const TargetReport = () => {
  const { t } = useTranslation(["common"]);
  const hasOrgSummaryreportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const breadCrumbItems = [
    {
      name: t(($) => $.common.sidebar["Target Report"]),
      href: "/user/report/target",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.common.sidebar["Reports"]),
    },
  ];

  if (
    hasOrgSummaryreportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }
  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <TargetReportBody />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/report/target")({
  component: TargetReport,
  staticData: { application: "Reports" },
});
